/**
 * Shared DTOs — the contract between the web UI and the server/BFF.
 *
 * Keep this layer free of platform-specific detail. Platform adapters map their
 * raw payloads INTO these normalized shapes; the UI only ever sees these.
 */

export type PlatformId = 'corrigo' | 'servicechannel';

export const PLATFORM_IDS: readonly PlatformId[] = ['corrigo', 'servicechannel'];

export const PLATFORM_LABELS: Record<PlatformId, string> = {
  corrigo: 'Corrigo Pro',
  servicechannel: 'ServiceChannel',
};

/** Per-platform connection state shown on the dashboard. */
export type ConnectionState =
  | 'connected'
  | 'not_configured' // app-level creds missing (Corrigo) — admin must set env
  | 'disconnected' // user has never connected (ServiceChannel)
  | 'reconnect_needed'; // refresh/token failed — user must re-auth (ServiceChannel)

export interface ConnectionStatus {
  platform: PlatformId;
  label: string;
  state: ConnectionState;
  /** Whether this platform requires a per-user OAuth connection (SC) vs app-level M2M (Corrigo). */
  perUser: boolean;
  /** Whether the quote-submit tool is usable for this platform right now. */
  canSubmit: boolean;
  /**
   * Corrigo only: whether FINAL quote submission to the customer is enabled.
   * When false, the tool runs in REVIEW MODE — the quote and its line items are
   * prepared and previewed, but the final submit is held so an admin can verify
   * the line items first. Undefined/true where it doesn't apply.
   */
  allowFinalSubmit?: boolean;
  /** Human-readable note, e.g. why submit is gated/disabled. */
  detail?: string;
  updatedAt?: string;
}

/** The two access levels for the backend login. */
export type UserRole = 'admin' | 'user';

/** The currently signed-in helper-site user. */
export interface SessionUser {
  id: string;
  email: string;
  isAdmin: boolean;
  role: UserRole;
  /** True until the user clears a forced password change (first login / reset). */
  mustChangePassword: boolean;
}

/** The currently signed-in helper-site user plus their platform connections. */
export interface SessionInfo {
  user: SessionUser | null;
  connections: ConnectionStatus[];
}

/** Admin-facing user record (never carries the password hash). */
export interface AdminUser {
  id: string;
  email: string;
  isAdmin: boolean;
  hasPassword: boolean;
  mustChangePassword: boolean;
  passwordUpdatedAt: string | null;
  createdAt: string;
}

/** Admin-editable password strength policy. */
export interface PasswordPolicy {
  minLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumber: boolean;
  requireSymbol: boolean;
}

/**
 * Human-readable requirement list for a policy — rendered as hints next to
 * password fields in the UI. Mirrors the server-side enforcement order.
 */
export function passwordPolicyRequirements(policy: PasswordPolicy): string[] {
  const reqs = [`At least ${policy.minLength} characters`];
  if (policy.requireUppercase) reqs.push('An uppercase letter');
  if (policy.requireLowercase) reqs.push('A lowercase letter');
  if (policy.requireNumber) reqs.push('A number');
  if (policy.requireSymbol) reqs.push('A symbol');
  return reqs;
}

/**
 * Normalized work-order summary returned by Platform.getWorkOrder.
 * This is the "is this the right WO?" confirmation payload.
 */
export interface WorkOrderSummary {
  platform: PlatformId;
  woNumber: string;
  /** Platform customer id (Corrigo Customer.Id) — scopes the price-list mapping. */
  customerId: string | null;
  client: string;
  location: string;
  status: string;
  nteAmount: number | null;
  currency: string; // ISO 4217 alpha (e.g. "USD")
  /** Whether a quote can be submitted against this WO right now. */
  canSubmitQuote: boolean;
  /** If !canSubmitQuote, why (e.g. "Work order is Recalled", "A quote already exists"). */
  reason?: string;
  /** Existing quote on the WO, if any. */
  existingQuote?: {
    amount: number | null;
    status: string; // e.g. Requested | Submitted | Approved | Rejected
    comment?: string;
  };
  /** Raw platform payload, for debugging/audit. Never rendered directly. */
  raw: unknown;
}

/** Which table of a Facil-IT proposal a line was parsed from. */
export type QuoteLineSection = 'incurred' | 'proposed';

export interface QuoteLineItem {
  description: string;
  /**
   * Wrapped detail lines that follow the row (e.g. a Materials row's itemized
   * list: "Masking supplies, Drywall supplies, …"). Kept SEPARATE from
   * `description` so item matching keys off the clean label ("Material") while
   * this text is carried onto the submitted Corrigo line's Description.
   * Nullable: the web client and price-list resolution layer round-trip an
   * explicit `null` for "no detail", which the submit Zod schema accepts.
   */
  descriptionDetail?: string | null;
  /**
   * The TRUE Corrigo quantity for this row, derived per the Facil-IT quantity
   * rule: for labor / man-hour rows it is the Man Hr value; for non-labor rows
   * (Trip Charge, Materials, …) it is the Qty column. Validated against
   * quantity × unitPrice == amount.
   */
  quantity: number;
  unitPrice: number;
  /** The row Total as printed on the proposal. */
  amount: number;
  /** Optional platform price-list item id, when chosen from the allowed list. */
  priceListItemId?: string;

  // ── Facil-IT proposal extraction (Part 1) ──────────────────────────────────
  /** Which proposal table this row came from (Incurred vs Proposed). */
  section?: QuoteLineSection;
  /** Raw Qty column from the proposal (often 1 for labor rows). */
  qtyColumn?: number | null;
  /** Man Hr column from the proposal; present (non-null) for labor rows. */
  manHr?: number | null;
  /**
   * Tax checkbox state parsed from the row's tax glyph (þ checked / ¨ unchecked).
   * Part of the Facil-IT line-item identity at quote-build time (picks the
   * Taxable vs Not-Taxable variant). Undefined when extraction was unreliable.
   */
  isTaxable?: boolean;
  /** True when the tax checkbox could not be read confidently — user must confirm. */
  taxUncertain?: boolean;
  /** True when quantity × unitPrice != amount within rounding — blocks submit. */
  totalMismatch?: boolean;
  /**
   * True when the printed Qty/Man Hr was missing or unusable and the quantity was
   * instead derived from Total ÷ Unit Price (so quantity × unitPrice == amount by
   * construction). Informational — surfaced to the user but does NOT block submit.
   */
  quantityDerived?: boolean;
}

/** Printed totals lifted verbatim from a Facil-IT proposal (the verification target). */
export interface ParsedTotals {
  /** Printed Sub Total per section table, in document order. */
  subTotals: number[];
  /** Printed Tax line, or null when the proposal prints none. */
  tax: number | null;
  /** Printed Total / Combined Total. */
  total: number | null;
}

/**
 * Independent reconciliation of the parsed line items against the proposal's
 * printed totals — the submit gate. `ok` must be true (or be user-overridden)
 * before a quote may be submitted.
 */
export interface ParsedReconciliation {
  ok: boolean;
  /** Sum of parsed row amounts. */
  computedSubtotal: number;
  /** Sum of taxable rows' amounts (basis for the tax check). */
  computedTaxableSubtotal: number;
  /** computedSubtotal + printed tax (the amount we'd submit). */
  computedTotal: number;
  printedSubtotal: number | null;
  printedTax: number | null;
  printedTotal: number | null;
  /** Human-readable parsed-vs-printed discrepancies (empty when ok). */
  discrepancies: string[];
}

/** A single allowed price-list item (Corrigo GET /api/invoice/pricelist). */
export interface PriceListItem {
  id: string;
  name: string;
  unitPrice: number | null;
  currency: string;
  uom?: string;
}

/** Input to Platform.submitQuote, after the user has confirmed. */
export interface QuoteSubmitInput {
  woNumber: string;
  /** The resolved integer Corrigo WorkOrderId (from the number→id picker). When
   *  set, the platform uses it directly instead of re-resolving the number. */
  workOrderId?: number;
  /**
   * Our internal proposal number (Facil-IT "PROPOSAL #"). ServiceChannel stamps
   * it onto the created proposal (body.proposalNumber); ignored by Corrigo.
   * Falls back to the work order number when absent.
   */
  proposalNumber?: string;
  /** Total quoted amount (the new NTE the provider is requesting). */
  amount: number;
  currency: string; // ISO 4217 alpha
  comment: string;
  /** Optional discount on the quote (Corrigo DiscountAmount). */
  discountAmount?: number;
  /**
   * Tax amount lifted from the proposal (ParsedTotals.tax). Corrigo has no
   * quote-level tax field, so this is submitted as a Category='Tax' quote line
   * sourced from the customer's Tax-type price-list item. The overall `amount`
   * stays tax-inclusive (the line items, including the Tax line, sum to it).
   */
  tax?: number;
  /** Optional service date (Corrigo ServiceDate, ISO 8601). */
  serviceDate?: string;
  lineItems?: QuoteLineItem[];
}

/**
 * One candidate from a work-order number search (Corrigo POST
 * /api/workOrder/search). Shown in the picker when a number matches >1 WO.
 */
export interface WorkOrderSearchMatch {
  workOrderId: number;
  workOrderNumber: string;
  status?: string;
  type?: string;
  customerName?: string;
  /** SLA priority (helps tell otherwise-similar work orders apart). */
  priorityName?: string;
  isEmergency?: boolean;
}

export interface QuoteSubmitResult {
  platform: PlatformId;
  woNumber: string;
  /** Resulting quote status, e.g. Corrigo "Submitted". */
  quoteStatus: string;
  amount: number;
  currency: string;
  submittedAt: string;
  auditId: string;
  /**
   * Platform-assigned id of the created quote/proposal (e.g. the ServiceChannel
   * proposal id returned by CreateProposal). Recorded in the audit trail so a
   * submission can always be traced back to the platform object it created.
   */
  platformQuoteId?: string | null;
}

/**
 * Draft produced by the PDF parser. Parsing is ASSISTIVE — every field is
 * editable in the UI before submit. `confidence` and `warnings` help the UI
 * flag fields the user should double-check.
 */
export interface ParsedQuote {
  /** Corrigo work order number — the value after "Client PO #". */
  woNumber: string | null;
  /** Our internal proposal number — the value after "PROPOSAL #" (audit only). */
  proposalNumber: string | null;
  amount: number | null;
  currency: string | null;
  /** The SERVICE DESCRIPTION narrative → Corrigo quote Comment (max 3072). */
  comment: string | null;
  /** True when the comment was truncated to fit Corrigo's 3072-char limit. */
  commentTruncated?: boolean;
  lineItems: QuoteLineItem[];
  /** Printed Sub Total(s) / Tax / Total lifted from the proposal. */
  totals: ParsedTotals;
  /** Parsed-vs-printed reconciliation — the submit gate. */
  reconciliation: ParsedReconciliation;
  /**
   * True when something must be resolved before submit: a row failed
   * qty × unit == total, the totals don't reconcile, or a tax checkbox was
   * unreadable. The UI surfaces these and blocks submit until cleared/confirmed.
   */
  blocking: boolean;
  /** 0..1 — rough heuristic confidence that the WO number was found correctly. */
  confidence: number;
  warnings: string[];
  /** Raw extracted text (truncated) for transparency/debugging. */
  rawTextPreview?: string;
}

/** Normalized error envelope (server → client). Detail is logged server-side only. */
export interface ApiError {
  code: string;
  userMessage: string;
  /** Present only in non-production responses. */
  detail?: string;
}

export interface ApiErrorResponse {
  error: ApiError;
}

export function isApiErrorResponse(v: unknown): v is ApiErrorResponse {
  return (
    typeof v === 'object' &&
    v !== null &&
    'error' in v &&
    typeof (v as { error?: unknown }).error === 'object'
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Price List Mapping tool (Corrigo)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Whether a customer's Corrigo price list could be pulled.
 * - 'available'     — a catalog has been pulled at least once.
 * - 'no_work_order' — the customer has no work order, so the catalog can't be
 *                     pulled yet (the only access path needs a WorkOrderId).
 * - null/undefined  — not yet attempted.
 */
export type PriceListState = 'available' | 'no_work_order';

/** A customer ("client") cached from the platform. */
export interface Customer {
  platform: PlatformId;
  customerId: string;
  name: string;
  /** Corrigo branch ids the customer spans (GET /api/customers); for entity mapping. */
  branchIds?: string[];
  /** Price-list availability (see PriceListState). */
  priceListState?: PriceListState | null;
  fetchedAt?: string;
}

/** A platform price-list option (Corrigo GET /api/invoice/pricelist), full record. */
export interface PriceListOption {
  priceListItemId: string;
  name: string;
  defaultRate: number | null;
  description: string | null;
  isRateReadOnly: boolean;
  taxCode: string | null;
  /** Corrigo RequestorServiceId — echoed back on quote line items. */
  requestorServiceId: string | null;
  /**
   * Corrigo line-item Type / Category, one of
   * ['Labor','Materials','Misc','Shipping','Services','PriceList','Tax'].
   * Used to locate the customer's Tax item so a Category='Tax' quote line can
   * carry the proposal's tax amount (Corrigo has no quote-level tax field).
   */
  type: string | null;
  active: boolean;
}

/**
 * A Facil-IT line item (our internal catalog entry). Taxable is part of the
 * item's IDENTITY: "Labor - Taxable" and "Labor - Not Taxable" are two distinct
 * items. `taxable` is true/false for the variants and null for tax-agnostic items.
 */
export interface InternalLineItem {
  id: string;
  code: string;
  name: string;
  defaultUnit: string | null;
  /** true = Taxable variant, false = Not-Taxable variant, null = tax-agnostic. */
  taxable: boolean | null;
  active: boolean;
}

/** A saved mapping of an internal item to a customer's price-list option. */
export interface LineItemMapping {
  internalLineItemId: string;
  priceListItemId: string;
  overrideRate: number | null;
  notes: string | null;
  updatedAt: string;
}

/** One row of the two-column mapper: an internal item + its mapping (if any). */
export interface MapperRow {
  internal: InternalLineItem;
  mapping: LineItemMapping | null;
  /** The chosen option's current details (null if unmapped or option went inactive). */
  option: PriceListOption | null;
  /** Effective rate that would be submitted (locked rate when IsRateReadOnly). */
  effectiveRate: number | null;
  rateLocked: boolean;
}

/** The mapper view for a customer. */
export interface MapperView {
  customer: Customer;
  priceListFetchedAt: string | null;
  rows: MapperRow[];
  /** All active price-list options (for the per-row dropdowns). */
  options: PriceListOption[];
  /** Active price-list options not yet mapped to any internal item. */
  unmappedOptions: PriceListOption[];
}

/** One Facil-IT item mapped to a Corrigo price-list item, with its rate. */
export interface CatalogMapEntry {
  internal: InternalLineItem;
  overrideRate: number | null;
  /** Effective rate that would be submitted (locked rate when IsRateReadOnly). */
  effectiveRate: number | null;
}

/**
 * One row of the Corrigo-item-centric mapper: a customer's Corrigo price-list
 * item and the Facil-IT line item(s) mapped to it. MANY Facil-IT items can map
 * to ONE Corrigo item (e.g. "Labor - Taxable" and "Labor - Not Taxable" both →
 * the client's single "Labor" item). Forward one-to-one still holds — each
 * Facil-IT item maps to at most one Corrigo item — so quote resolution
 * (PDF line → Facil-IT item → Corrigo item) stays unambiguous.
 */
export interface CatalogMapRow {
  option: PriceListOption;
  /** The Facil-IT items mapped to this Corrigo item (0..N). */
  mapped: CatalogMapEntry[];
  rateLocked: boolean;
}

/** The Corrigo-item-centric mapper view for a customer. */
export interface CatalogMapperView {
  customer: Customer;
  priceListFetchedAt: string | null;
  priceListState: PriceListState | null;
  /** One row per Corrigo price-list item (active options). */
  rows: CatalogMapRow[];
  /** The full Facil-IT master list (for the per-row typeahead). */
  internalItems: InternalLineItem[];
  /** Facil-IT items already mapped to some Corrigo item (each maps to one, so
   *  it can't be added to another row — forward one-to-one). */
  usedInternalItemIds: string[];
  /** Count of active options with no Facil-IT mapping. */
  unmappedCount: number;
}

/** One row of a bulk mapping import (CSV/JSON). */
export interface MappingImportRow {
  priceListItemId: string;
  /** Facil-IT item, matched by code or name. Empty clears the mapping. */
  facilitCode?: string | null;
  facilitName?: string | null;
  overrideRate?: number | null;
}

/** Per-row outcome of validating a bulk mapping import. */
export interface MappingImportRowResult {
  row: number;
  priceListItemId: string;
  status: 'ok' | 'cleared' | 'error';
  /** Resolved Facil-IT item code (when ok). */
  facilitCode?: string;
  message?: string;
}

/** Result of validating (and optionally applying) a bulk mapping import. */
export interface MappingImportResult {
  applied: boolean;
  total: number;
  okCount: number;
  errorCount: number;
  results: MappingImportRowResult[];
  /** Facil-IT names referenced by the import that don't exist yet (offer to add). */
  missingFacilit: string[];
}

/** Resolution of one parsed quote line item against the customer's mappings. */
export interface ResolvedQuoteLine {
  parsedDescription: string;
  /** Wrapped detail text (e.g. a materials list) to carry onto the Corrigo line. */
  descriptionDetail?: string | null;
  quantity: number;
  /** The tax-checkbox state used to pick the Taxable/Not-Taxable Facil-IT variant. */
  isTaxable?: boolean;
  /** Set when resolved. */
  internalCode?: string;
  internalName?: string;
  priceListItemId?: string;
  priceListItemName?: string;
  /** Corrigo RequestorServiceId for the mapped option, when known. */
  requestorServiceId?: string | null;
  /** Corrigo TaxCode echoed onto the submitted quote line. */
  taxCode?: string | null;
  rate?: number;
  rateLocked?: boolean;
  /**
   * True when the parsed description matched no Facil-IT item and was resolved via
   * the "Misc" catch-all fallback instead. The line still submits, but is flagged
   * so the user can confirm the Misc routing rather than it happening silently.
   */
  viaFallback?: boolean;
  /** Why it could not be resolved, if unresolved. */
  unmappedReason?:
    | 'no_internal_item'
    | 'no_taxable_variant'
    | 'no_mapping'
    | 'option_inactive';
}

/** Preview of how a quote's line items resolve before submit. */
export interface QuoteLinePreview {
  customerId: string | null;
  resolved: ResolvedQuoteLine[];
  /** True when every parsed line resolved to a mapped, active option. */
  allResolved: boolean;
}

/** Audit-log row exposed to the UI (no secrets). */
export interface AuditEntry {
  id: string;
  platform: PlatformId;
  woNumber: string;
  amount: number | null;
  currency: string | null;
  result: 'success' | 'error';
  message?: string;
  createdAt: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Admin reports
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Minutes of manual work saved by one successful quote submission. Multiplied by
 * the quote count to give the "hours saved" headline on the reports page. A flat
 * placeholder rate for now — change it here and every report follows.
 */
export const MINUTES_SAVED_PER_QUOTE = 7.5;

/**
 * Successful quote submissions for one day, keyed by portal (`PlatformId`).
 * Every known portal is always present, zero-filled, so the UI can render a
 * stable set of columns; an unrecognized portal seen in the audit log is added
 * as an extra key rather than being dropped, so the per-portal counts always
 * add up to `quotes`.
 */
export type QuotesByPortal = Record<string, number>;

/** One calendar day's submission tallies (bucketed in the report's timezone). */
export interface SubmissionsPerDayRow {
  /** Calendar day, `YYYY-MM-DD`. */
  day: string;
  /** Quotes that submitted successfully, across all portals. */
  quotes: number;
  /** Quote submit attempts that failed. */
  quotesFailed: number;
  /** Successful quote submissions broken down per portal. */
  quotesByPortal: QuotesByPortal;
  /** Checklists pushed back to Corrigo successfully. */
  checklists: number;
  /** Checklist push attempts that failed. */
  checklistsFailed: number;
}

/** Range-wide totals, mirroring the per-day row shape. */
export interface SubmissionsTotals {
  quotes: number;
  quotesFailed: number;
  quotesByPortal: QuotesByPortal;
  checklists: number;
  checklistsFailed: number;
}

/**
 * "Submissions per day" report over an inclusive `[from, to]` day range, covering
 * both quote submissions (split per portal) and checklist submissions. `rows` is
 * gap-filled so every day in the range is present (zeros included).
 */
export interface SubmissionsReport {
  /** Inclusive start day, `YYYY-MM-DD`. */
  from: string;
  /** Inclusive end day, `YYYY-MM-DD`. */
  to: string;
  /** IANA timezone the days were bucketed in. */
  timezone: string;
  rows: SubmissionsPerDayRow[];
  totals: SubmissionsTotals;
  /** The rate `hoursSaved` was derived from, so the UI can show its basis. */
  minutesSavedPerQuote: number;
  /** Hours saved across the range: successful quotes x `minutesSavedPerQuote`. */
  hoursSaved: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// CorrigoPro Checklist tool (Corrigo "AM" Direct API)
//
// A separate Corrigo surface from the quote-submit integration above: it reads a
// work order's procedures/steps, exposes the hidden step instructions, lets the
// internal team fill in checklist responses, and pushes them back via the API.
// These normalized shapes are all the UI ever sees — never the raw Corrigo
// payload, and never the server-only bearer token.
// ─────────────────────────────────────────────────────────────────────────────

/** A checklist step's response status (Corrigo step Status enum). */
export type CorrigoStepStatus = 'Open' | 'Done' | 'NA' | 'Unknown';

export const CORRIGO_STEP_STATUSES: readonly CorrigoStepStatus[] = [
  'Open',
  'Done',
  'NA',
  'Unknown',
];

/**
 * Corrigo work-order statuses in which procedure steps may be written back.
 * Pushing is blocked (read-only) for any other status.
 */
export const CORRIGO_PUSHABLE_STATUSES: readonly string[] = [
  'Accepted',
  'CheckedIn',
  'Paused',
  'OnHold',
];

/** One normalized checklist step under a procedure. */
export interface CorrigoStep {
  id: number;
  order: number;
  /** Display step number (Corrigo Step.Number or the parsed leading code). */
  stepNumber: string;
  /** Human title extracted from the raw description (e.g. "Storage Temperature"). */
  title: string;
  /** The raw, multi-line Step.Description as Corrigo returned it. */
  rawDescription: string;
  /** Hidden instructions HTML from /procedure/step/ (StepsDetails[].Instructions). */
  instructionsHtml?: string;
  /** Sanitized plain-text instructions (the part before any "Notes:" section). */
  instructionsText?: string;
  /** Sanitized plain-text notes (the part after "Notes:"), when present. */
  notesText?: string;
  /** Current Corrigo response status. */
  status: CorrigoStepStatus;
  /** Current Corrigo response value (free text / number / boolean as string). */
  value?: string;
  /** Current Corrigo response comment. */
  comment?: string;
  isRequired?: boolean;
  stepType?: string;
  dataType?: string | null;
  /** Allowed values for choice-type steps, when Corrigo provides them. */
  options?: string[];
  isNa?: boolean;
}

/** One normalized procedure (group of steps), usually tied to an asset. */
export interface CorrigoProcedure {
  id: number;
  order: number;
  name: string;
  assetId?: number;
  assetName?: string;
  status?: string;
  summary?: string;
  operationalRisks?: string;
  backoutPlan?: string;
  steps: CorrigoStep[];
}

export interface CorrigoServiceLocation {
  occupantName?: string;
  address?: string;
  spaceName?: string;
}

/** The normalized checklist for a work order — the core UI payload. */
export interface CorrigoChecklist {
  workOrderNumber: string;
  workOrderId: number;
  status: string;
  customerName?: string;
  serviceLocation?: CorrigoServiceLocation;
  procedures: CorrigoProcedure[];
  /** Server-computed: whether step responses may be pushed in the current status. */
  canPush?: boolean;
  /** If !canPush, the read-only reason to surface in the UI. */
  pushBlockedReason?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Document upload (Corrigo) — quote-submit surface
//
// Corrigo has TWO document mechanisms, both bound to the work order (by
// WorkOrderId), not to the quote object:
//   - General Attachments  — POST /api/attachments        (supporting docs)
//   - Required Documents    — POST /api/requiredDocument    (customer-mandated
//                             "slots"/drawers that MUST be satisfied per-slot)
// These normalized shapes are all the UI ever sees. Uploads always go through
// the server/BFF (the browser never calls Corrigo directly); type and size are
// validated server-side.
// ─────────────────────────────────────────────────────────────────────────────

/** Corrigo attachment DocumentType enum (defaults to Document). */
export type CorrigoDocumentType =
  | 'Document'
  | 'Picture'
  | 'Signature'
  | 'InvoicePrintout'
  | 'ServiceContract'
  | 'Misc';

export const CORRIGO_DOCUMENT_TYPES: readonly CorrigoDocumentType[] = [
  'Document',
  'Picture',
  'Signature',
  'InvoicePrintout',
  'ServiceContract',
  'Misc',
];

/** Required Document status. `Incomplete` = the slot still needs a file. */
export type RequiredDocumentStatus =
  | 'Incomplete'
  | 'NoReviewNeeded'
  | 'NeedsReview'
  | 'Reviewed';

/** The attachment currently satisfying a Required Document slot, if any. */
export interface RequiredDocumentAttachment {
  attachmentId: string;
  name?: string;
  mimeType?: string;
  size?: number;
  documentType?: string;
  title?: string;
  visibility?: string;
  senderName?: string;
}

/**
 * One customer-defined Required Document "slot" for a work order. Files meant to
 * satisfy a slot MUST be uploaded through the Required Document endpoint, matched
 * to the slot by its `documentId`.
 */
export interface RequiredDocumentSlot {
  documentId: string;
  description?: string;
  title?: string;
  status: RequiredDocumentStatus;
  hasAdditionalWork?: boolean;
  documentDate?: string;
  /** The attachment satisfying this slot, or null when still Incomplete. */
  attachment?: RequiredDocumentAttachment | null;
}

/**
 * What document targets exist for a work order. The UI renders one labeled
 * dropzone per Required Document slot PLUS a general "Additional documents" zone
 * when slots exist; only the general zone when none exist.
 */
export interface WorkOrderDocumentTargets {
  woNumber: string;
  /** The integer Corrigo WorkOrderId documents bind to. */
  workOrderId: number;
  status: string;
  customerName?: string;
  /** False for Rejected/Recalled work orders (documents cannot be attached). */
  canUpload: boolean;
  /** If !canUpload, the reason to surface in the UI. */
  reason?: string;
  /** Required Document slots (empty when the WO has none). */
  requiredDocuments: RequiredDocumentSlot[];
  hasRequiredDocuments: boolean;
}

/** Result of uploading a single file (one Corrigo call per file). */
export interface DocumentUploadResult {
  ok: boolean;
  /** Which Corrigo mechanism handled the file. */
  path: 'general' | 'required';
  woNumber: string;
  workOrderId: number;
  filename: string;
  size: number;
  mimeType: string;
  documentType?: CorrigoDocumentType;
  /** The Required Document slot id (only when path = 'required'). */
  documentId?: string;
  /** Corrigo AttachmentId returned on success. */
  attachmentId?: string;
  messageId?: string;
  auditId: string;
}

/** Corrigo hard limit: 20 MB per file (enforced client AND server side). */
export const CORRIGO_MAX_DOCUMENT_BYTES = 20 * 1024 * 1024;

/** Allowed upload MIME types (PDF / JPG / PNG / Word docs). */
export const CORRIGO_ALLOWED_DOCUMENT_MIME_TYPES: readonly string[] = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

/** Allowed upload file extensions (fallback when a browser omits the MIME type). */
export const CORRIGO_ALLOWED_DOCUMENT_EXTENSIONS: readonly string[] = [
  '.pdf',
  '.jpg',
  '.jpeg',
  '.png',
  '.doc',
  '.docx',
];

// ─────────────────────────────────────────────────────────────────────────────
// Location Match tool
//
// Reconcile a platform's location list (Corrigo "CP" or ServiceChannel "SC")
// against the internal "FIT" master list, producing a key of platform-ID → FIT-ID.
// The user uploads ONE workbook with two sheets, maps which columns hold the id /
// address / city / state / zip for each side (no hardcoded column letters), and
// downloads a MATCHED workbook. These shapes are all the UI ever sees.
// ─────────────────────────────────────────────────────────────────────────────

/** Which platform's location list is being reconciled. */
export type LocationMatchPlatform = 'cp' | 'sc';

/** One sheet discovered in the uploaded workbook (for the column-mapping UI). */
export interface LocationMatchSheet {
  name: string;
  /** Header cells from the first row, in column order. */
  headers: string[];
  /** Data row count (excluding the header row). */
  rowCount: number;
}

/** Response to inspecting an uploaded workbook before matching. */
export interface LocationMatchInspectResult {
  sheets: LocationMatchSheet[];
}

/**
 * User-chosen column mapping for one side (FIT or platform). Each value is a
 * header name from that sheet. `id` and `address` are required; `city`/`state`/
 * `zip` are optional but improve the fuzzy fallback and city-mismatch detection.
 */
export interface LocationColumnMap {
  id: string;
  address: string;
  city?: string;
  state?: string;
  zip?: string;
}

/** Full configuration for a match run (sent alongside the workbook). */
export interface LocationMatchConfig {
  platform: LocationMatchPlatform;
  fitSheet: string;
  platformSheet: string;
  fit: LocationColumnMap;
  platformCols: LocationColumnMap;
}

/** How a row was matched. */
export type LocationMatchMethod = 'ID' | 'Address' | 'None';

/** Verdict for a single platform location. */
export type LocationMatchStatus = 'Match' | 'Close' | 'Mismatch' | 'No Reliable Match';

/** One reconciled row in the output Key sheet. */
export interface LocationMatchKeyRow {
  fitId: string | null;
  platformId: string;
  fitAddress: string;
  platformAddress: string;
  score: number;
  cityAlert: string;
  method: LocationMatchMethod;
  status: LocationMatchStatus;
}

/** Summary returned to the UI after a run (the workbook itself is downloaded). */
export interface LocationMatchSummary {
  platform: LocationMatchPlatform;
  filename: string;
  totalPlatformRows: number;
  /** Rows kept after CP smart-dedup (equals totalPlatformRows for SC). */
  matchedRows: number;
  counts: Record<LocationMatchStatus, number>;
  byMethod: Record<LocationMatchMethod, number>;
  cityAlerts: number;
}

/** A single match returned by the work-order number search. */
export interface CorrigoSearchMatch {
  workOrderId: number;
  workOrderNumber: string;
  status?: string;
  customerName?: string;
  /** True when the number matched exactly (vs. a fuzzy/partial Corrigo match). */
  exact: boolean;
}

export interface CorrigoSearchResponse {
  number: string;
  matches: CorrigoSearchMatch[];
  /** The date windows actually searched (for transparency in the UI). */
  windowsSearched: number;
}

/** One step update the internal team wants to push back to Corrigo. */
export interface CorrigoStepUpdateInput {
  id: number;
  status: CorrigoStepStatus;
  value?: string;
  comment?: string;
  /** Only for Money-typed steps. */
  currency?: string;
}

/** Per-step outcome of a push. */
export interface CorrigoStepPushResult {
  id: number;
  ok: boolean;
  /** Confirmed status after re-fetch, when available. */
  status?: CorrigoStepStatus;
  /** Confirmed comment after re-fetch, when available. */
  comment?: string;
  value?: string;
  /** Error message if this step failed. */
  error?: string;
}

/** Result of pushing one or more step updates (with confirmation re-fetch). */
export interface CorrigoPushResult {
  workOrderNumber: string;
  workOrderId: number;
  results: CorrigoStepPushResult[];
  /** The re-fetched checklist confirming the new state. */
  checklist: CorrigoChecklist;
  auditId: string;
}

/**
 * One step response read back from a filled-in vendor checklist PDF. Only the
 * fields the vendor actually filled are present (so importing patches, never
 * clobbers). `conflict` flags a step where both Done and N/A were ticked.
 */
export interface CorrigoImportedStep {
  stepId: number;
  status?: CorrigoStepStatus;
  value?: string;
  comment?: string;
  conflict?: boolean;
}

/**
 * Result of importing a filled vendor PDF. The server matches the PDF's step
 * ids against the live work order; `responses` are the matched, fill-bearing
 * steps and `unmatchedStepIds` are PDF ids that don't belong to this WO.
 */
export interface CorrigoImportResult {
  workOrderNumber: string;
  workOrderId: number;
  responses: CorrigoImportedStep[];
  unmatchedStepIds: number[];
  /** Steps the PDF ticked both Done and N/A — surfaced for the user to resolve. */
  conflictStepIds: number[];
}
